/**
 * Swiss QWERTZ (Windows KBDSF / Microsoft "Swiss French" reference, the SN 074021 board Switzerland
 * ships for both language drivers): maps a physical code to the unshifted character that key emits.
 * The Swiss board is QWERTZ - Y and Z are swapped from US Qwerty (KeyY emits Z, KeyZ emits Y). The
 * French-Latin accents sit unshifted on the OEM area: e-grave on the US-"[" key, e-acute on the
 * US-";" key, a-grave on the US-"'" key (German umlauts ride their Shift layer). The number-row
 * apostrophe and circumflex, the section sign left of "1", and the diaeresis right of "[" are the
 * board's other OEM legends; circumflex and diaeresis are dead keys, given as their legend glyph
 * only. The ISO key left of Y ("<>", IntlBackslash) is outside the US-Qwerty code block. Letters
 * uppercase by table convention; accented letters stay lowercase.
 * Source: Microsoft KBDSF layout (learn.microsoft.com/globalization/keyboards/kbdsf_2.html) and
 * kbdlayout.info/KBDSF, cross-checked against the Swiss SN 074021 keyboard standard.
 */
export const SWISS_CODE_TO_CHAR: Record<string, string> = {
  Backquote: "§", // section sign (U+00A7)
  Digit1: "1",
  Digit2: "2",
  Digit3: "3",
  Digit4: "4",
  Digit5: "5",
  Digit6: "6",
  Digit7: "7",
  Digit8: "8",
  Digit9: "9",
  Digit0: "0",
  Minus: "'", // apostrophe (U+0027)
  Equal: "^", // dead circumflex (U+005E)
  KeyQ: "Q",
  KeyW: "W",
  KeyE: "E",
  KeyR: "R",
  KeyT: "T",
  KeyY: "Z", // Z/Y swap
  KeyU: "U",
  KeyI: "I",
  KeyO: "O",
  KeyP: "P",
  BracketLeft: "è", // e-grave (U+00E8)
  BracketRight: "¨", // dead diaeresis (U+00A8)
  Backslash: "$", // dollar (U+0024); ISO key left of Enter
  KeyA: "A",
  KeyS: "S",
  KeyD: "D",
  KeyF: "F",
  KeyG: "G",
  KeyH: "H",
  KeyJ: "J",
  KeyK: "K",
  KeyL: "L",
  Semicolon: "é", // e-acute (U+00E9)
  Quote: "à", // a-grave (U+00E0)
  KeyZ: "Y", // Z/Y swap
  KeyX: "X",
  KeyC: "C",
  KeyV: "V",
  KeyB: "B",
  KeyN: "N",
  KeyM: "M",
  Comma: ",",
  Period: ".",
  Slash: "-", // hyphen-minus (U+002D)
};

/**
 * The .SC2Hotkeys serialization token per code where Swiss QWERTZ's token diverges from charToken of
 * its character. SC2 binds these OEM keys by virtual-key, so the file names the
 * key by its Swiss VK, not the accent/symbol it emits: the apostrophe key serializes as "BracketOpen"
 * (VK_OEM_4), the e-grave key as "SemiColon" (VK_OEM_1), and so on. Like the UK ISO board, TheCore
 * remaps any US-backslash binding onto Enter (the ANSI "\\|" key has no home on this ISO layout), so
 * the Backslash code serializes as the positional token "Enter". Recovered from TheCore's real
 * US-Qwerty -> Switzerland pair (both hands) and verified to reproduce those files command-for-command.
 * Letters (including the Y/Z swap) and digits are omitted: a letter matches charToken(map[code]) via
 * the char path, and a digit token is positional. The map is injective so every key round-trips.
 * Backquote's "§" key is VK_OEM_2, which SC2 names "Slash" - witnessed by the left-hand file (the
 * right-hand file's one "Grave" binding, ChatAllies, is left unconverted, a documented source quirk).
 */
export const SWISS_CODE_TO_TOKEN: Record<string, string> = {
  Backquote: "Slash", // §, VK_OEM_2
  Minus: "BracketOpen", // ', VK_OEM_4
  Equal: "BracketClose", // ^ dead key, VK_OEM_6
  BracketLeft: "SemiColon", // è, VK_OEM_1
  BracketRight: "Grave", // ¨ dead key, VK_OEM_3
  Backslash: "Enter", // $, ISO: US backslash bindings remap to Enter
  Semicolon: "Apostrophe", // é, VK_OEM_7
  Quote: "BackSlash", // à, VK_OEM_5
  Slash: "Minus", // -, VK_OEM_MINUS
};
